import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';

const HD_PROFILE_ROLE_LINES: readonly number[] = [1, 2, 3, 4, 5, 6];
const HD_PROFILE_ROLE_LINE_COLORS: Readonly<Record<number, string>> = {
  1: '#1983bf', 2: '#1983bf', 3: '#1983bf',
  4: '#1c658e', 5: '#1c658e', 6: '#1c658e',
};

/** Externalisatie- en internalisatie-rolpoorten, in de Figma-kolomvolgorde. */
const HD_EXTERNALIZATION_GATES: readonly number[] = [7, 1, 27];
const HD_INTERNALIZATION_GATES: readonly number[] = [13, 10, 59];

/** Domein per rolpoort: collectief (7/13), individueel (1/10), tribaal (27/59). */
const HD_ROLE_GATE_DOMAIN: Readonly<Record<number, 'collective' | 'individual' | 'tribal'>> = {
  7: 'collective', 13: 'collective',
  1: 'individual', 10: 'individual',
  27: 'tribal', 59: 'tribal',
};

/** Eén rol-blok (bv. "7.1 De Autoritaire") in de content-taal. */
interface ProfileRoleVm {
  readonly key: string;
  readonly title: string;
  /** "[Collectief Externalisatie]"-tag, samengesteld uit de labels. */
  readonly tag: string;
  readonly bullets: readonly string[];
}

/** Eén profiellijn-kaart met de 6 rollen van die lijn. */
interface ProfileRolesCardVm {
  readonly line: number;
  readonly color: string;
  readonly name: string;
  readonly keyword: string;
  readonly keyword2: string;
  readonly meta: string;
  readonly externalization: readonly ProfileRoleVm[];
  readonly internalization: readonly ProfileRoleVm[];
}

/**
 * Pagina die de Profielen & rollen cheat sheet toont, nagebouwd uit het
 * Figma-design "A3 HD Profiles Roles Cheat Sheet" (zonder A3-header/branding-
 * footer). Per profiellijn één brede kaart met links de externalisatie-rollen
 * (7.x, 1.x, 27.x) en rechts de internalisatie-rollen (13.x, 10.x, 59.x).
 *
 * Content komt uit de `human-design/profile-roles`-scope via
 * {@link HdGateInfoService.cheatSheetTextIn}; taalwissel via de gedeelde
 * content-taal-picker, nog niet vertaalde EN-velden vallen terug op NL.
 */
@Component({
  selector: 'hd-profile-roles-cheat-sheet-page',
  standalone: false,
  templateUrl: './hd-profile-roles-cheat-sheet-page.component.html',
  styleUrl: './hd-profile-roles-cheat-sheet-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdProfileRolesCheatSheetPageComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly pageTitle = computed(() => this.text('pageTitle'));
  protected readonly cardTitle = computed(() => this.text('cardTitle'));

  protected readonly cards = computed<readonly ProfileRolesCardVm[]>(() =>
    HD_PROFILE_ROLE_LINES.map(line => ({
      line,
      color: HD_PROFILE_ROLE_LINE_COLORS[line],
      name: this.text(`lines.${line}.name`),
      keyword: this.text(`lines.${line}.keyword`),
      keyword2: this.text(`lines.${line}.keyword2`),
      meta: this.text(`lines.${line}.meta`),
      externalization: HD_EXTERNALIZATION_GATES.map(gate => this.role(gate, line, 'externalization')),
      internalization: HD_INTERNALIZATION_GATES.map(gate => this.role(gate, line, 'internalization')),
    })),
  );

  constructor() {
    // Beide content-talen eager laden zodat de taalwissel én de NL-fallback
    // meteen correcte tekst opleveren.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  protected print(): void {
    window.print();
  }

  private role(gate: number, line: number, direction: 'externalization' | 'internalization'): ProfileRoleVm {
    const key = `${gate}-${line}`;
    return {
      key,
      title: `${gate}.${line} ${this.text(`roles.${key}.name`)}`,
      tag: `[${this.text(`labels.${HD_ROLE_GATE_DOMAIN[gate]}`)} ${this.text(`labels.${direction}`)}]`,
      bullets: this.list(`roles.${key}.bullets`),
    };
  }

  /** Scope-tekst in de content-taal, met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string): string {
    const lang = this.lang();
    const value = this.gateInfo.cheatSheetTextIn('profileRoles', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('profileRoles', key, 'nl');
  }

  /** Scope-string-array in de content-taal, met dezelfde NL-fallback als {@link text}. */
  private list(key: string): readonly string[] {
    const lang = this.lang();
    const value = this.gateInfo.cheatSheetListIn('profileRoles', key, lang);
    if (value.length > 0 || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetListIn('profileRoles', key, 'nl');
  }
}
